import PropTypes from "prop-types";
import React from "react";
import { connect } from "react-redux";
import {
  findLineItem,
  findReplacementLineItems,
  findSection,
  findSubLineItems,
  getSectionPath
} from "shared/selectors/unit";
import { Button, Grid, Header, Icon, Label, Segment } from "semantic-ui-react";

import { FormattedMessage } from "react-intl";
import FormatCurrency from "shared/components/currency/FormatCurrency";
import { handleScrollTop } from "shared/helpers/scrollTo";
import { toggleConfirmationDialog } from "shared/components/dialogs/ConfirmationDialogContainer";
import { roomBookController } from "shared/actions/roomBookController";
import { withRouter } from "../../helpers/withRouter";
import CatalogPage from "./CatalogPage";
import CardGrid from "../cardGrid/CardGrid";
import ProductOptions from "./ProductOptions";
import LineItemList from "./LineItemList";

import { navigationButton } from "../../helpers/navigationButton";

import SubLineItemGrid from "./SubLineItemGrid";

import "./lineItemPage.scss";

class LineItemPage extends React.Component {
  renderTopMessage = () => {
    const { lineItem, subLineItems, replacableLineItem } = this.props;
    if (
      !lineItem.isPriceOnRequest &&
      (lineItem.isReplacement ||
        (lineItem.isOptional && subLineItems.length > 1))
    ) {
      return (
        <Segment data-component="base-price">
          <Grid>
            <Grid.Row>
              <Grid.Column width={16}>
                <Header as="h3" className="left floated element">
                  Basispreis {lineItem.title}
                </Header>
                <Label className="right floated element price-tag">
                  <Icon name="plus" />{" "}
                  <FormatCurrency amount={lineItem.totalMin} />
                </Label>
              </Grid.Column>
            </Grid.Row>
            {lineItem.isReplacement && (
              <Grid.Row>
                <Grid.Column width={16}>
                  <p>
                    Der Entfall der Standardleistung "{replacableLineItem.title}
                    " wurde im Basispreis berücksichtigt.
                  </p>
                </Grid.Column>
              </Grid.Row>
            )}
          </Grid>
        </Segment>
      );
    }
  };

  renderBody = () => {
    const { subLineItems, lineItem } = this.props;
    if (subLineItems.length === 1 && !lineItem.isCompactDisplay) {
      return this.renderProductOptions(subLineItems[0]);
    }
    return this.renderSubLineItemMenu(subLineItems);
  };

  renderBottomMessage = () => {
    const { lineItem } = this.props;

    if (lineItem.isOptional && !lineItem.isProgressed) {
      return (
        <div className="margin spacious top">
          <Button
            id="remove-line-item"
            icon="remove"
            basic
            size="small"
            onClick={this.deactivateLineItem}
            content={`Sonderwunschpaket ${lineItem.title} entfernen`}
          />
        </div>
      );
    }
  };

  renderReplacementLineItems = () => {
    const { replacementLineItems, lineItem } = this.props;

    if (replacementLineItems.length) {
      return (
        <div data-component="replacementLineItems">
          <Header size="large">Alternative Ausstattungsmöglichkeiten</Header>
          <LineItemList
            lineItems={replacementLineItems}
            replacableLineItem={lineItem}
          />
        </div>
      );
    }
  };

  renderProductOptions = subLineItem => {
    const { lineItem } = this.props;
    const field = lineItem.isReplacement ? "relativeTotal" : "total";
    return <ProductOptions subLineItem={subLineItem} priceField={field} />;
  };

  renderSubLineItemMenu = subLineItems => {
    const { lineItem, lineItemPath } = this.props;
    if (lineItem.isCompactDisplay) {
      return <SubLineItemGrid subLineItems={subLineItems} />;
    }
    const items = subLineItems.map(subLineItem => {
      const isBasePrice = lineItem.isOptional && !subLineItem.isDecided;

      return {
        key: subLineItem.id,
        title: subLineItem.title,
        imageUrl: subLineItem.thumbUrl,
        imageClass: "productImage",
        total: isBasePrice ? 0 : subLineItem.totalRelative,
        isBasePrice: false,
        isPriceOnRequest: subLineItem.isPriceOnRequest,
        url: `${lineItemPath}/sub_line_item/${subLineItem.id}`,
        button: navigationButton({ ...subLineItem, isOptional: false })
      };
    });

    return <CardGrid items={items} />;
  };

  deactivateLineItem = () => {
    const {
      lineItem,
      subLineItems,
      ctrl,
      confirmationDialog,
      sectionPath,
      router
    } = this.props;
    const subLineItemIds = subLineItems.map(sli => sli.id);
    confirmationDialog({
      title: (
        <FormattedMessage
          id="buyerPortal.roomBook.optionalLineItem.actions.remove.title"
          values={{ ...lineItem }}
        />
      ),
      content: (
        <FormattedMessage
          id="buyerPortal.roomBook.optionalLineItem.actions.remove.message"
          values={{ ...lineItem }}
        />
      ),
      buttons: [
        {
          id: "remove",
          label: "meta.actions.unlink",
          onClick: cb => {
            ctrl.deselectProducts(subLineItemIds).then(() => {
              ctrl.deactivateLineItem(lineItem.id).then(() => {
                cb();
                handleScrollTop();
                router.push(sectionPath);
              });
            });
          }
        },
        {
          id: "cancel",
          label: "meta.actions.cancel",
          basic: true
        }
      ]
    });
  };

  render() {
    return (
      <CatalogPage data-component="lineItemPage">
        {this.renderTopMessage()}
        {this.renderBody()}
        {this.renderBottomMessage()}
        {this.renderReplacementLineItems()}
      </CatalogPage>
    );
  }
}

LineItemPage.propTypes = {
  subLineItems: PropTypes.array.isRequired
};

const mapStateToProps = (state, props) => {
  const { sectionId, lineItemId } = props.params;
  const section = findSection(state)(sectionId);
  const lineItem = findLineItem(state)(lineItemId);
  const replacableLineItem = findLineItem(state)(lineItem.replacementToId);
  const subLineItems = findSubLineItems(state)(lineItemId).filter(sli => {
    return sli.isVisible && sli.isConfigurable;
  });
  const replacementLineItems = findReplacementLineItems(state)(
    lineItemId
  ).filter(rli => rli.isVisible);
  const sectionPath = getSectionPath(props);
  const { pathname } = props.location;
  return {
    section,
    lineItem,
    subLineItems,
    sectionPath,
    replacementLineItems,
    replacableLineItem,
    lineItemPath: pathname
  };
};

const mapDispatchToProps = (dispatch, props) => {
  return {
    ctrl: roomBookController(dispatch, props.params.id),
    confirmationDialog: toggleConfirmationDialog(dispatch)
  };
};

export default withRouter(
  connect(mapStateToProps, mapDispatchToProps)(LineItemPage)
);
